import React, { useMemo, useState } from 'react';
import defaultClubs from '../../data/clubs.json';
import '../PlayerCombiner/PlayerCombiner.css';

// Los escudos viven en img/teams con el mismo nombre que la key del club en clubs.json.
const loadTeamImages = () => {
    const context = require.context('../../img/teams', false, /\.jpg$/);
    const images = {};
    context.keys().forEach((key) => {
        images[key.replace('./', '').replace('.jpg', '')] = context(key);
    });
    return images;
};

// "aston_villa" / "arsenal-de-sarandi" → "Aston Villa" / "Arsenal De Sarandi".
export const prettyClubName = (key) => String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// El json de clubes guarda un "tier" por club: cuanto más alto, más plantel histórico
// tiene y por lo tanto más fácil es encontrarle cruces con otro equipo. La escala NO es la
// misma en los dos juegos (mundo llega a 5, Argentina a 3), así que la cantidad de
// estrellas se calcula desde los datos en vez de fijarla en 5.
const Tier = ({ value, max }) => (
    <span className="b11-club-tier" title={`Nivel ${value} de ${max}`}>
        {Array.from({ length: max }, (_, i) => (
            <span key={i} className={i < value ? '' : 'off'}>★</span>
        ))}
    </span>
);

const TeamSelector = ({ onSelect, clubs = defaultClubs }) => {
    const [query, setQuery] = useState('');
    const teamImages = useMemo(loadTeamImages, []);

    const maxTier = useMemo(
        () => Object.values(clubs).reduce((m, v) => Math.max(m, Number(v) || 0), 0) || 5,
        [clubs],
    );

    const entries = useMemo(() => {
        const norm = query.trim().toLowerCase();
        return Object.entries(clubs)
            // allLPF vive en clubs.json pero es el modo aleatorio, no un club: va aparte
            // (botón "Cruces al azar"), no como una tarjeta más de la grilla.
            .filter(([key]) => key !== 'allLPF')
            .map(([key, tier]) => ({ key, tier, nombre: prettyClubName(key) }))
            .filter((c) => !norm || c.nombre.toLowerCase().includes(norm) || c.key.toLowerCase().includes(norm))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [query, clubs]);

    // 'allLPF' NO es un club: es el modo aleatorio que ya entiende el backend
    // (getPossibleSolutions devuelve pares [equipoA, equipoB] de toda la liga en vez
    // de cruces contra un club fijo). Por eso el botón manda esa key tal cual.
    const pickRandom = () => onSelect('allLPF');

    return (
        <div className="b11-shell">
            <div className="b11-sel-hero">
                <span className="b11-sel-kicker">⚽ Build your XI</span>
                <h1 className="b11-sel-title">Armá tu once</h1>
                <p className="b11-sel-sub">
                    Elegí un club y completá una formación con jugadores que hayan vestido
                    <b> las dos camisetas</b> de cada cruce.
                </p>

                <div className="b11-steps">
                    <div className="b11-step">
                        <div className="b11-step-n">1</div>
                        <div className="b11-step-t">Elegí tu club (o dejá que el azar decida)</div>
                    </div>
                    <div className="b11-step">
                        <div className="b11-step-n">2</div>
                        <div className="b11-step-t">Te toca un cruce de dos equipos y una formación</div>
                    </div>
                    <div className="b11-step">
                        <div className="b11-step-n">3</div>
                        <div className="b11-step-t">Buscá un jugador que haya jugado en ambos</div>
                    </div>
                </div>
            </div>

            <div className="b11-sel-tools">
                <label className="b11-search-clubs">
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar club…"
                        aria-label="Buscar club"
                    />
                </label>
                <button type="button" className="b11-random-btn" onClick={pickRandom}>
                    <span className="b11-dice">🎲</span> Cruces al azar
                </button>
            </div>

            <div className="b11-club-grid">
                {entries.map(({ key, tier, nombre }) => (
                    <button
                        type="button"
                        key={key}
                        className="b11-club-card"
                        onClick={() => onSelect(key)}
                        title={nombre}
                    >
                        <img
                            className="b11-club-crest"
                            src={teamImages[key]}
                            alt=""
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                        <span className="b11-club-name">{nombre}</span>
                        <Tier value={tier} max={maxTier} />
                    </button>
                ))}
                {entries.length === 0 && (
                    <p className="b11-club-empty">No encontramos ningún club con “{query}”.</p>
                )}
            </div>
        </div>
    );
};

export default TeamSelector;
